import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GlobalNotification, NotificationCategory, NotificationPreferences } from '@/types/notification.types';

interface NotificationState {
  notifications: GlobalNotification[];
  unreadCount: number;
  preferences: NotificationPreferences;

  // Actions
  addNotification: (notification: Omit<GlobalNotification, 'id' | 'timestamp'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  markCategoryAsRead: (category: NotificationCategory) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  clearOlderThan: (days: number) => void;
  updatePreferences: (preferences: Partial<NotificationPreferences>) => void;
  reset: () => void;

  // Selectors
  getUnreadByCategory: (category: NotificationCategory) => number;
  getNotificationsByCategory: (category: NotificationCategory) => GlobalNotification[];
}

const defaultPreferences: NotificationPreferences = {
  enableToasts: true,
  enableSounds: true,
  categories: {
    matches: true,
    tournaments: true,
    ladder: true,
    gathers: true,
    friends: true,
    messages: true,
    system: true,
  },
};

const initialState = {
  notifications: [] as GlobalNotification[],
  unreadCount: 0,
  preferences: defaultPreferences,
};

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
  ...initialState,

  addNotification: (notification) => {
    const id = `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = new Date().toISOString();

    set((state) => {
      const newNotification = { ...notification, id, timestamp, read: false };
      return {
        notifications: [newNotification, ...state.notifications].slice(0, 100),
        unreadCount: state.unreadCount + 1,
      };
    });
  },

  markAsRead: (id) =>
    set((state) => {
      const notification = state.notifications.find((n) => n.id === id);
      if (!notification || notification.read) return state;

      return {
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    }),

  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    })),

  markCategoryAsRead: (category) =>
    set((state) => {
      const unreadInCategory = state.notifications.filter(
        (n) => n.category === category && !n.read
      ).length;
      return {
        notifications: state.notifications.map((n) =>
          n.category === category ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - unreadInCategory),
      };
    }),

  removeNotification: (id) =>
    set((state) => {
      const notification = state.notifications.find((n) => n.id === id);
      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        unreadCount: notification && !notification.read
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
      };
    }),

  clearAll: () =>
    set({
      notifications: [],
      unreadCount: 0,
    }),

  clearOlderThan: (days) =>
    set((state) => {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      const filtered = state.notifications.filter(
        (n) => new Date(n.timestamp) > cutoffDate
      );

      const removedUnread = state.notifications.filter(
        (n) => new Date(n.timestamp) <= cutoffDate && !n.read
      ).length;

      return {
        notifications: filtered,
        unreadCount: Math.max(0, state.unreadCount - removedUnread),
      };
    }),

  updatePreferences: (preferences) =>
    set((state) => ({
      preferences: { ...state.preferences, ...preferences },
    })),

  reset: () => set(initialState),

  getUnreadByCategory: (category) => {
    return get().notifications.filter((n) => n.category === category && !n.read).length;
  },

  getNotificationsByCategory: (category) => {
    return get().notifications.filter((n) => n.category === category);
  },
}),
    {
      name: 'te-notifications',
      partialize: (state) => ({
        notifications: state.notifications.slice(0, 50), // persist max 50
        unreadCount: state.unreadCount,
        preferences: state.preferences,
      }),
    }
  )
);
